import * as React from 'react';
import styled, { withTheme } from 'styled-components';
import { rem } from 'polished';
import { __ } from 'react-i18n';
import {
  FlexCol,
  FlexRow,
  FlexRowCenterVertical,
} from '../../Defaults/Flexbox/Flexbox';
import { Title } from '../General/Title';
import { TextP } from '../../Defaults/Text';
import { FormInput } from '../Form/FormInput';
import { SvgIcon, IconType } from '../General/SvgIcon';
import { Tag, TagType } from '../Product/Tag';
import InfoRow from './InfoRow';
import { BannerType, BannerWithImgButton } from './BannerWithImgButton';
import { SpecTable } from './SpecTable';
import { SpecsList } from './SpecsList';
import { DownloadsList } from './DownloadsList';
import { MobileInfoRoller } from './MobileInfoRoller';
import { NoItemsWrapper } from '../General/NoItemsWrapper';
import { prop } from '../../../utilities';

interface Props {
  desc: string;
  attribs: any[];
  downloads: any[];
  accessoriesRef: any;
  relatedRef: any;
  showAssembling: boolean;
  assemblingLink: string;
  servisInfo: {
    showServis: boolean;
    servisLink: string;
    showServisMax: boolean;
    servisMaxLink: string;
  };
}

function BottomInfo({
  desc,
  attribs,
  downloads,
  accessoriesRef,
  relatedRef,
  showAssembling,
  assemblingLink,
  servisInfo,
}: Props) {
  const initialActive =
    desc && typeof desc === 'string'
      ? 0
      : attribs && attribs.length > 0
      ? 1
      : downloads && downloads.length > 0
      ? 2
      : null;
  const [activeId, setActiveId] = React.useState(initialActive);

  const handleItemClick = (id, ref) => {
    if (!ref) {
      setActiveId(id);
    } else if (ref && ref.current) {
      window.scrollTo({
        behavior: 'smooth',
        top: ref.current.offsetTop,
      });
    }
  };

  const upperItems = [
    {
      id: 0,
      name: __('Popis produktu'),
      show: desc && typeof desc === 'string',
    },
    {
      id: 1,
      name: __('Technická špecifikácia'),
      show: attribs && attribs.length > 0,
    },
    {
      id: 2,
      name: __('Na stiahnutie'),
      numberOf: downloads.length,
      show: downloads && downloads.length > 0,
    },
    {
      id: 3,
      name: __('Príslušenstvo'),
      show: accessoriesRef && accessoriesRef.current,
      ref: accessoriesRef,
    },
    {
      id: 4,
      name: __('Podobné produkty'),
      show: relatedRef && relatedRef.current,
      ref: relatedRef,
    },
  ];

  const renderDesc = visible => {
    return (
      <DescWrapper visible={visible}>
        <DescBannerWrapper>
          {desc && typeof desc === 'string' && (
            <Desc dangerouslySetInnerHTML={{ __html: desc }} />
          )}
          {showAssembling ? (
            <BannerWithImgButton
              image={'/images/zemplin/montaz_krbov.png'}
              url={assemblingLink}
            />
          ) : prop(servisInfo, 'showServis') ? (
            <BannerWithImgButton
              image={'/images/zemplin/servis.png'}
              url={prop(servisInfo, 'servisLink')}
              type={BannerType.SERVIS}
            />
          ) : prop(servisInfo, 'showServisMax') ? (
            <BannerWithImgButton
              image={'/images/zemplin/servis_max.png'}
              url={prop(servisInfo, 'servisMaxLink')}
              type={BannerType.SERVIS_MAX}
            />
          ) : null}
        </DescBannerWrapper>
        {attribs && attribs.length > 0 && (
          <SpecTable
            attribs={attribs.slice(0, 3)}
            // tslint:disable-next-line:jsx-no-lambda
            onClickAll={() => setActiveId(1)}
          />
        )}
      </DescWrapper>
    );
  };

  const renderSpecs = visible => {
    return (
      <SpecsWrapper visible={visible && attribs.length > 0}>
        {attribs.length > 0 ? (
          <SpecsList attribs={attribs} />
        ) : (
          <NoItemsWrapper
            height={50}
            text={__('Produkt neobsahuje žiadnu špecifikáciu')}
          />
        )}
      </SpecsWrapper>
    );
  };

  const renderDownloads = visible => {
    return (
      <DownloadsWrapper visible={visible}>
        {downloads.length > 0 ? (
          <DownloadsList downloads={downloads} />
        ) : (
          <NoItemsWrapper
            height={50}
            text={__('Produkt neobsahuje žiadne dokumenty na stiahnutie')}
          />
        )}
      </DownloadsWrapper>
    );
  };

  return (
    <>
      <DesktopWrapper>
        <UpperWrapper>
          {upperItems.map(
            item =>
              item.show && (
                <UpperItem
                  // tslint:disable-next-line:jsx-no-lambda
                  onClick={() => handleItemClick(item.id, item.ref)}
                  active={activeId === item.id}
                  key={item.id}
                >
                  {item.name}
                  {item.numberOf ? (
                    <NumberSpan>{item.numberOf}</NumberSpan>
                  ) : null}
                </UpperItem>
              ),
          )}
        </UpperWrapper>
        <BottomWrapper>
          {renderDesc(activeId === 0)}
          {renderSpecs(activeId === 1)}
          {renderDownloads(activeId === 2)}
        </BottomWrapper>
      </DesktopWrapper>
      <MobileWrapper>
        {desc && typeof desc === 'string' ? (
          <MobileInfoRoller initialIsOpen={true} title={__('Popis produktu')}>
            {renderDesc(true)}
          </MobileInfoRoller>
        ) : null}
        {attribs && attribs.length > 0 ? (
          <MobileInfoRoller title={__('Technická špecifikácia')}>
            {renderSpecs(true)}
          </MobileInfoRoller>
        ) : null}
        {downloads && downloads.length > 0 ? (
          <MobileInfoRoller
            numberOf={downloads.length}
            title={__('Na stiahnutie')}
          >
            {renderDownloads(true)}
          </MobileInfoRoller>
        ) : null}
      </MobileWrapper>
    </>
  );
}

const DesktopWrapper = styled(FlexCol)`
  width: 100%;
  color: #aaaaaa;
  position: relative;
  margin-right: ${rem(32)};
  margin-bottom: ${rem(56)};

  ${({ theme }) => theme.mediab.m760`
   display:none;
  `}
`;

const MobileWrapper = styled(FlexCol)`
  display: none;
  width: 100%;
  color: #aaaaaa;
  position: relative;
  margin-right: ${rem(32)};
  margin-bottom: ${rem(32)};

  ${({ theme }) => theme.mediab.m760`
   display:flex;
  `}
`;

const UpperWrapper = styled(FlexRow)`
  width: 100%;
  border-bottom: ${({ theme }) => theme.borders.primary};
  margin-bottom: ${rem(32)};
`;

const UpperItem = styled(FlexRowCenterVertical)<{ active: boolean }>`
  padding-bottom: ${rem(24)};
  margin-right: ${rem(56)};
  border-bottom: ${rem(2)} solid
    ${({ active, theme }) => (active ? theme.colors.primary : 'transparent')};
  color: ${({ active, theme }) =>
    active ? theme.colors.primary : theme.colors.textPrimary};
  font-size: ${rem(20)};
  line-height: ${rem(28)};
  font-family: ${({ theme }) => theme.font.primary};
  cursor: pointer;
  transition: color 0.2s ease, border 0.2s ease;
  height: 60px;

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

export const NumberSpan = styled.span`
  color: ${({ theme }) => theme.colors.primary};
  background-color: ${({ theme }) => theme.colors.navSecondary};
  font-family: ${({ theme }) => theme.font.tertiary};
  padding: ${rem(3)} ${rem(12)};
  margin-left: ${rem(12)};
  border-radius: 50%;
  font-size: ${rem(12)};
`;

const BottomWrapper = styled.div``;

const Text = styled(TextP)`
  font-size: ${rem(14)};
  font-weight: 400;
  line-height: ${rem(22)};
`;

const DescBannerWrapper = styled(FlexCol)`
  max-width: ${rem(808)};
  width: 100%;
`;

const Desc = styled.div`
  margin-bottom: ${rem(48)};
  line-height: ${rem(26)};
  font-size: ${rem(14)};
  font-weight: 400;
  color: ${({ theme }) => theme.colors.textPrimary};

  & > p {
    font-size: ${rem(14)};
    font-weight: 400;
    color: ${({ theme }) => theme.colors.textPrimary};
    margin: 0;
    line-height: ${rem(26)};
  }

  ${({ theme }) => theme.mediab.m760`
    margin-bottom: 0;
  `}
`;

const VisiblityWrapper = styled.div<{ visible: boolean }>`
  visibility: ${({ visible }) => (visible ? 'visible' : 'hidden')};
  height: ${({ visible }) => (visible ? 'auto' : 0)};
  display: ${({ visible }) => (visible ? 'block' : 'none')};
`;

const DescWrapper = styled(VisiblityWrapper)`
  display: flex;
  justify-content: space-between;

  ${({ theme }) => theme.mediab.m760`
    padding: ${rem(12)} 0 ${rem(8)};
  `}
`;

const SpecsWrapper = styled(VisiblityWrapper)``;
const DownloadsWrapper = styled(VisiblityWrapper)``;

export { BottomInfo };
